import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2, ClipboardList, Loader2 } from "lucide-react";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import Button from "../../../../shared/ui/button/Button";
import { deleteTherapeuticPlan, listCoupleTherapeuticPlans, listPatientTherapeuticPlans } from "../../application/therapeuticPlans";
import type { TherapeuticPlan } from "../../../therapeutic_plan";


interface PatientViewTherapeuticPlansProps {
  patientID: string;
  isCouple?: boolean;
  pageSize?: number;
}

export default function PatientViewTherapeuticPlans({
  patientID,
  isCouple = false,
  pageSize = 5,
}: PatientViewTherapeuticPlansProps) {
  const { t } = useTranslation();
  const [therapeuticPlans, setTherapeuticPlans] = useState<TherapeuticPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 🔹 Paginación
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const navigate = useNavigate();

  const fetchTherapeuticPlans = useCallback(async () => {
    try {
      setLoading(true);

      const params: Record<string, unknown> = {
        offset,
        limit: pageSize,
      };

      const res = isCouple
        ? await listCoupleTherapeuticPlans(patientID, params)
        : await listPatientTherapeuticPlans(patientID, params);
      const data = res.therapeutic_plans || res;
      setTherapeuticPlans(data);
      setTotal(res.total || data.length);
    } catch (err) {
      console.error("Error fetching therapeutic plans:", err);
      setError(t("therapeuticPlan.errorLoading"));
    } finally {
      setLoading(false);
    }
  }, [patientID, isCouple, offset, pageSize, t]);

  useEffect(() => {
    fetchTherapeuticPlans();
  }, [fetchTherapeuticPlans]);

  // 🗑️ Delete
  const handleDelete = async (id: string) => {
    if (!confirm(t("therapeuticPlan.deleteConfirm"))) return;
    try {
      await deleteTherapeuticPlan(id);
      setTherapeuticPlans((prev) => prev.filter((s) => s.id !== id));
      // Re-fetch to update total and handle pagination edge cases if needed
      fetchTherapeuticPlans();
    } catch (err) {
      console.error("Error deleting therapeutic plan:", err);
      alert(t("therapeuticPlan.deleteError"));
    }
  };

  // ✏️ Edit
  const handleEdit = (therapeuticPlan: TherapeuticPlan) => {
    navigate(`/therapeutic-plans/${therapeuticPlan.id}`);
  };

  // 🔹 Controles de paginación
  const handlePrevPage = () => setOffset((prev) => Math.max(prev - pageSize, 0));
  const handleNextPage = () => setOffset((prev) => prev + pageSize);



  if (error)
    return <p className="text-center text-red-500 py-10">{error}</p>;

  return (
    <div className="glass-effect glass-border border rounded-3xl p-8 shadow-theme-md space-y-8">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-brand-500/10 text-brand-500 flex items-center justify-center">
          <ClipboardList className="w-6 h-6" />
        </div>
        <h3 className="text-xl font-bold text-content-primary">
          {t("patient.therapeuticPlans")}
        </h3>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 relative">
          {/* Layout loading overlay */}
          {loading && therapeuticPlans.length > 0 && (
            <div className="absolute inset-0 bg-white/50 z-10 dark:bg-gray-900/50 flex justify-center items-center rounded-2xl backdrop-blur-[1px]">
              <Loader2 className="animate-spin h-8 w-8 text-brand-600" />
            </div>
          )}

          {loading && therapeuticPlans.length === 0 ? (
            // SKELETON LOADER (Card Style)
            Array.from({ length: pageSize }).map((_, i) => (
              <div key={i} className="p-5 bg-gray-50/50 dark:bg-white/[0.02] rounded-2xl animate-pulse flex items-center gap-4 border border-gray-100 dark:border-white/5">
                <div className="w-12 h-12 rounded-xl bg-gray-200 dark:bg-gray-800"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-1/3"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/4"></div>
                </div>
              </div>
            ))
          ) : therapeuticPlans.length > 0 ? (
            therapeuticPlans.map((tp) => (
              <div
                key={tp.id}
                className="group relative flex flex-col sm:flex-row items-center justify-between p-5 bg-white/40 dark:bg-white/[0.02] border border-gray-100 dark:border-white/5 rounded-2xl shadow-theme-xs hover:shadow-theme-md transition-all duration-300 hover:border-brand-400/50"
              >
                <div className="flex flex-1 items-center gap-4 w-full">
                  {/* Icon */}
                  <div
                    className="flex items-center justify-center w-12 h-12 rounded-xl shrink-0 bg-brand-50 text-brand-500 dark:bg-brand-500/10 dark:text-brand-400 shadow-theme-xs group-hover:scale-110 transition-transform"
                  >
                    <ClipboardList className="w-6 h-6 opacity-80" />
                  </div>

                  {/* Plan Details */}
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/therapeutic-plans/${tp.id}`}
                      className="block hover:translate-x-1 transition-transform"
                    >
                      <h3 className="text-base font-bold text-content-primary group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                        {tp.title || t("therapeuticPlan.untitled")}
                      </h3>
                    </Link>
                    {tp.timeline && tp.timeline.length > 0 && (
                      <div className="flex flex-wrap gap-x-2 gap-y-1 mt-1.5">
                        {tp.timeline.sort((a, b) => a.order - b.order).map((phase, idx) => (
                          <div key={phase.id} className="flex items-center">
                            <span 
                              className={`text-xs font-medium ${phase.completed ? 'text-zen-indigo' : 'text-content-tertiary'}`}
                              style={phase.completed ? { color: '#6A4FD8' } : {}}
                            >
                              {phase.title || t("therapeuticPlan.untitledPhase")}
                            </span>
                            {idx < tp.timeline!.length - 1 && (
                              <span className="ml-2 text-xs text-gray-300 dark:text-gray-700">/</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 mt-4 sm:mt-0 ml-auto sm:ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleEdit(tp)}
                    className="inline-flex items-center justify-center p-2.5 rounded-xl bg-white dark:bg-white/5 text-content-tertiary hover:text-brand-500 hover:shadow-theme-xs border border-gray-100 dark:border-white/5 transition-all"
                    title={t("common.edit")}
                  >
                    <Pencil className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(tp.id)}
                    className="inline-flex items-center justify-center p-2.5 rounded-xl bg-white dark:bg-white/5 text-content-tertiary hover:text-red-500 hover:shadow-theme-xs border border-gray-100 dark:border-white/5 transition-all"
                    title={t("common.delete")}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="p-12 text-center bg-gray-50/30 dark:bg-white/[0.01] rounded-3xl border border-dashed border-gray-200 dark:border-white/5">
              <ClipboardList className="w-12 h-12 text-gray-300 dark:text-gray-700 mx-auto mb-4 opacity-50" />
              <p className="text-content-tertiary font-medium">{t("therapeuticPlan.notFound")}</p>
            </div>
          )}
        </div>

        {/* 🔹 Footer de paginación */}
        <Pagination
          offset={offset}
          total={total}
          pageSize={pageSize}
          onPrev={handlePrevPage}
          onNext={handleNextPage}
        />

        {/* 🔹 Button: create therapeutic plan for this patient */}
        <div className="mt-4 flex justify-center">
          <Button
            onClick={() =>
              navigate(`/therapeutic-plans/new?patient=${patientID}`)
            }
            className="w-1/2 sm:w-1/3"
          >
            {t("therapeuticPlan.newPlan")}
          </Button>
        </div>
      </div>
    </div>
  );
}
